import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  useTheme,
  alpha,
  LinearProgress,
  Chip,
  IconButton,
  Alert,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector, useStore } from "react-redux";
import { getFlashcard } from "../../state/slices/flashcard";
import { startStudySession, updateProgress, clearProgress } from "../../state/slices/flashcardProgress";
import Loading from "../../components/loading/loading";
import { Flip, ArrowBack, CheckCircle, Cancel, School } from "@mui/icons-material";

const MASTERY_LEVELS = ["new", "learning", "mastered"];

const StudyFlashcards = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { currentFlashcard, loading: flashcardLoading } = useSelector((state) => state.flashcard);
  const { currentSession, loading: progressLoading, errorMsg } = useSelector((state) => state.flashcardProgress);
  const store = useStore();
  
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [studiedCards, setStudiedCards] = useState(new Set());
  const [isUpdatingProgress, setIsUpdatingProgress] = useState(false);

  const location = useLocation();
  const isReview = new URLSearchParams(location.search).get('review') === 'true';
  
  // Root navigation paths - don't show back button on these
  const rootPaths = ['/quiz/my', '/quiz/student', '/flashcard/my', '/flashcard/student', '/progress', '/', '/request', '/student-request', '/tutor-class-room', '/student-class-room'];
  const showBackButton = !rootPaths.includes(location.pathname);

  useEffect(() => {
    dispatch(getFlashcard(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (currentFlashcard) {
      // Clear any existing session first to ensure we start fresh
      if (currentSession && currentSession.flashcardId?._id?.toString() !== id && currentSession.flashcardId?.toString() !== id) {
        dispatch(clearProgress());
      }
      
      // Start new session if we don't have one for this flashcard
      if (!currentSession || (currentSession.flashcardId?._id?.toString() !== id && currentSession.flashcardId?.toString() !== id)) {
        dispatch(startStudySession({ flashcardId: id, review: isReview })).catch((error) => {
          // Error will be handled by errorMsg in state
        });
      }
    }
  }, [currentFlashcard, currentSession, id, dispatch, isReview]);

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleMasteryUpdate = async (masteryLevel) => {
    // Get the latest session from Redux store (fresh read)
    const latestState = store.getState();
    const latestSession = latestState.flashcardProgress?.currentSession || currentSession;
    const sessionToUse = latestSession || currentSession;
    
    if (!sessionToUse || !currentFlashcard || isUpdatingProgress) {
      return;
    }
    
    const currentCard = currentFlashcard.cards[currentCardIndex];
    if (!currentCard) {
      console.error('Current card not found');
      return;
    }
    
    // Find card progress by matching cardId (more reliable than index)
    const currentCardId = currentCard._id?.toString() || currentCard._id;
    
    let cardProgress = null;
    let cardProgressIndex = -1;
    
    // First try to find by cardId
    if (currentCardId && sessionToUse.cardProgress) {
      cardProgressIndex = sessionToUse.cardProgress.findIndex(cp => {
        const progressCardId = cp.cardId?._id?.toString() || cp.cardId?.toString() || cp.cardId;
        return progressCardId === currentCardId;
      });
      
      if (cardProgressIndex >= 0) {
        cardProgress = sessionToUse.cardProgress[cardProgressIndex];
      }
    }
    
    // Fallback to index if cardId match fails
    if (!cardProgress && sessionToUse.cardProgress && sessionToUse.cardProgress[currentCardIndex]) {
      cardProgress = sessionToUse.cardProgress[currentCardIndex];
      cardProgressIndex = currentCardIndex;
    }
    
    if (!cardProgress) {
      console.error('Card progress not found for card at index:', currentCardIndex, 'cardId:', currentCardId);
      return;
    }
    
    const progressId = sessionToUse._id;
    // Use the cardId from the progress record
    const cardId = cardProgress.cardId?._id?.toString() || cardProgress.cardId?.toString() || cardProgress.cardId;
    
    if (!cardId) {
      console.error('Card ID not found in progress');
      return;
    }
    
    setIsUpdatingProgress(true);
    try {
      const result = await dispatch(updateProgress({
        progressId,
        cardId,
        masteryLevel
      }));
      
      if (result.type && result.type.includes('fulfilled')) {
        setStudiedCards(new Set([...studiedCards, cardId]));
        setIsFlipped(false);
        // Move to next card - the Redux state will be updated automatically
        handleNext();
      }
    } catch (error) {
      console.error('Error updating mastery:', error);
    } finally {
      setIsUpdatingProgress(false);
    }
  };

  const handleNext = () => {
    setIsFlipped(false);
    if (currentCardIndex < (currentFlashcard?.cards?.length || 0) - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
    } else {
      // Finished studying
      navigate("/flashcard/student");
    }
  };

  if (flashcardLoading || (!currentFlashcard && !errorMsg)) {
    return <Loading />;
  }

  if (errorMsg && !currentSession && !currentFlashcard) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 64px)",
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          pb: 4,
        }}
      >
        <Container maxWidth="md" sx={{ pt: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
          <Button variant="contained" onClick={() => navigate("/flashcard/student")}>
            {t('common.back')}
          </Button>
        </Container>
      </Box>
    );
  }

  if (!currentFlashcard) {
    return <Loading />;
  }

  const currentCard = currentFlashcard.cards[currentCardIndex];
  const progress = ((currentCardIndex + 1) / currentFlashcard.cards.length) * 100;
  
  // Get the latest session for display
  const latestState = store.getState();
  const displaySession = latestState.flashcardProgress?.currentSession || currentSession;
  
  // Find card progress by matching cardId (more reliable than index)
  const currentCardId = currentCard._id?.toString() || currentCard._id;
  let cardProgress = null;
  
  if (currentCardId && displaySession?.cardProgress) {
    cardProgress = displaySession.cardProgress.find(cp => {
      const progressCardId = cp.cardId?._id?.toString() || cp.cardId?.toString() || cp.cardId;
      return progressCardId === currentCardId;
    });
  }
  
  // Fallback to index if cardId match fails
  if (!cardProgress && displaySession?.cardProgress?.[currentCardIndex]) {
    cardProgress = displaySession.cardProgress[currentCardIndex];
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        pb: 4,
      }}
    >
      <Container maxWidth="md" sx={{ pt: 4 }}>
        {/* Back Button - only show if not on root navigation page */}
        {showBackButton && (
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 3,
              color: 'text.secondary',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            {t('common.back')}
          </Button>
        )}

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {errorMsg}
          </Alert>
        )}

        {/* Header */}
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <School sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 0.5 }}>
              {currentFlashcard.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('flashcard.studyMode') || 'Study your flashcards'}
            </Typography>
          </Box>
        </Box>

        {/* Progress Bar */}
        <Box 
          sx={{ 
            mb: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: 'white',
            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center', mb: 1.5 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              {t('flashcard.card')} {currentCardIndex + 1} {t('common.of')} {currentFlashcard.cards.length}
            </Typography>
            {cardProgress && (
              <Chip 
                label={cardProgress.masteryLevel} 
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
              },
            }}
          />
        </Box>

        {/* Flashcard */}
        <Card
          elevation={0}
          sx={{
            minHeight: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            position: "relative",
            mb: 3,
            borderRadius: 3,
            border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            bgcolor: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.02)',
              borderColor: theme.palette.primary.main,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`,
            },
          }}
          onClick={handleFlip}
        >
          <CardContent sx={{ textAlign: "center", width: "100%", p: 4 }}>
            {!isFlipped ? (
              <>
                <Box
                  sx={{
                    mb: 3,
                    display: 'inline-flex',
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {t('flashcard.front')}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}>
                  {currentCard.front}
                </Typography>
                {currentCard.hint && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {t('flashcard.hint')}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentCard.hint}
                    </Typography>
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
                  {t('flashcard.clickToFlip') || 'Click to flip'}
                </Typography>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    mb: 3,
                    display: 'inline-flex',
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {t('flashcard.back')}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                  {currentCard.back}
                </Typography>
              </>
            )}
            <Box sx={{ mt: 4 }}>
              <Button
                variant="outlined"
                startIcon={<Flip />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFlip();
                }}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  fontWeight: 600,
                }}
              >
                {isFlipped ? t('flashcard.showFront') : t('flashcard.showBack')}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Mastery Buttons */}
        {isFlipped && (
          <Box 
            sx={{ 
              display: "flex", 
              gap: 2, 
              justifyContent: "center",
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: 'white',
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              onClick={() => handleMasteryUpdate("new")}
              disabled={isUpdatingProgress || progressLoading}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 600,
              }}
            >
              {t('flashcard.stillLearning')}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => handleMasteryUpdate("learning")}
              disabled={isUpdatingProgress || progressLoading}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 600,
              }}
            >
              {t('flashcard.gettingThere')}
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={() => handleMasteryUpdate("mastered")}
              disabled={isUpdatingProgress || progressLoading}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 600,
                boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
              }}
            >
              {t('flashcard.mastered')}
            </Button>
          </Box>
        )}

        {/* Navigation Buttons */}
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setIsFlipped(false);
              if (currentCardIndex > 0) {
                setCurrentCardIndex(currentCardIndex - 1);
              }
            }}
            disabled={currentCardIndex === 0 || isUpdatingProgress || progressLoading}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 600,
            }}
          >
            {t('flashcard.previous')}
          </Button>
          <Button 
            variant="contained" 
            onClick={handleNext}
            disabled={isUpdatingProgress || progressLoading}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          >
            {currentCardIndex === currentFlashcard.cards.length - 1 ? t('flashcard.finish') : t('flashcard.next')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default StudyFlashcards;

